import {
  AsyncPipe,
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgFor,
  NgIf,
  NgTemplateOutlet,
  SlicePipe,
} from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SvgComponent } from '@app/base-components';
import { HabaCurrencyPipe, HabaQaId } from '@app/helpers';
import { BreadcrumbService, OciService } from '@app/services';
import { I18nModule, WindowRef } from 'haba-core';
import { PrefixboxService } from 'haba-emporix';
import { BREAKPOINT, BreakpointService } from 'haba-storefrontlib';
import { isEqual } from 'lodash-es';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, startWith, take, takeUntil } from 'rxjs/operators';
import { SearchHistoryPersistenceService } from 'src/app/search/services/search-history-persistence.service';
import { SearchInputComponent } from '../search-input/search-input.component';

@Component({
  selector: 'app-search-wrapper',
  templateUrl: './search-wrapper.component.html',
  styleUrls: ['./search-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    SearchInputComponent,
    SvgComponent,
    NgFor,
    NgTemplateOutlet,
    NgClass,
    SlicePipe,
    I18nModule,
    AsyncPipe,
    HabaCurrencyPipe,
  ],
})
export class SearchWrapperComponent
  implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy
{
  @Input() baseId: HabaQaId;
  @Input() searchAsYouType: boolean = true;
  @Input() asIcon: boolean = false;
  @Input() asSearchIcon: boolean = false;
  @Output() closeSearch = new EventEmitter<void>();
  @Output() linkClick = new EventEmitter<string>();

  @ViewChild('suggestionsInput', { static: false }) public suggestionsInput: SearchInputComponent;
  @ViewChild('mainInput', { static: false }) public mainInput: SearchInputComponent;

  public suggestions: {
    keywords: any[];
    categories: any[];
    products: any[];
    contentPages: any[];
    questions: any[];
  } = {
    keywords: [],
    categories: [],
    products: [],
    contentPages: [],
    questions: [],
  };
  public brandSuggestions: string[];
  public query: string = '';
  public hasQuery: boolean = false;
  public searchInputFocus: boolean = false;
  public isDesktop: boolean;
  public isClickInsideSearchWrapper: boolean = false;

  private isBrowser: boolean;
  private device: 'desktop' | 'mobile';
  private unsubscribe$ = new Subject<void>();

  apiResponse: any[];

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private searchHistoryPersistenceService: SearchHistoryPersistenceService,
    private router: Router,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
    private prefixboxService: PrefixboxService,
    private breakpointService: BreakpointService,
    private breadcrumbService: BreadcrumbService,
    private winRef: WindowRef,
    protected ociService: OciService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.breakpointService
      .isUp(BREAKPOINT.md)
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((isDesktop) => {
        this.device = isDesktop ? 'desktop' : 'mobile';
        this.isDesktop = isDesktop;
      });
  }

  @HostListener('keydown.escape') closeOnEscape() {
    this.onWrapperClose();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.asIcon?.currentValue) {
      this.onFocus();
    }
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
      this.prefixboxService.scriptInitiated$.pipe(startWith(false)),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(([, scriptInitiated]) => scriptInitiated)
      )
      .subscribe(() => {
        if (this.winRef.isBrowser()) {
          const newPath = window.location.pathname.replace(/^\/[^/]+/, '');
          const variationMatch = window.location.hash.match(/variationId=([^&]+)/);
          const referer = newPath + (variationMatch ? `#variationId=${variationMatch[1]}` : '');
          (window as any).prefixboxAnalytics?.(
            'boxload',
            'autocomplete',
            {
              btr: this.prefixboxService.getAutocompleteBoxTracker(this.device),
            },
            { Referer: referer }
          );
        }
      });
  }

  ngAfterViewChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  public onFocus(): void {
    this.searchInputFocus = true;
    if (this.isBrowser) {
      setTimeout(() => this.suggestionsInput.searchBox.nativeElement.focus(), 0);
    }
    // remove scroll from background page
    this.renderer.addClass(this.document.body, 'blocked-scroll');
  }

  public mainInputReset(): void {
    this.mainInput.searchBox.nativeElement.value = '';
  }

  public onCloseSearch() {
    if (this.isClickInsideSearchWrapper) {
      this.isClickInsideSearchWrapper = false;
      return;
    }

    this.searchInputFocus = false;
    this.mainInput?.searchBox?.nativeElement?.focus();
    this.closeSearch.emit();
    this.restoreScroll();
  }

  public redirectTo(hit: any): void {
    const [path, fragment] = hit.url.split('#');
    this.breadcrumbService.clearItems();
    this.router.navigate([path], { fragment });
    this.trackHit(hit);
    this.onWrapperClose();
  }

  public onSearchWrapperMouseDown(): void {
    this.isClickInsideSearchWrapper = true;
  }

  public onSuggestionClick(hit: any): void {
    this.router.navigate(['search'], { queryParams: { query: hit.text } });
    this.searchHistoryPersistenceService.add(hit.text);

    this.trackHit(hit);
    this.onWrapperClose();
  }

  private trackHit(hit: any): void {
    (window as any).prefixboxAnalytics?.('query', 'selected', {
      pattern: this.suggestionsInput.searchBox.nativeElement.value,
      btr: this.prefixboxService.getAutocompleteBoxTracker(this.device),
      clickedSuggestion: hit,
      position: hit.index + 1,
      content: this.apiResponse,
    });
  }

  public formSubmit() {
    const query = this.suggestionsInput.searchBox.nativeElement.value.trim();
    if (!query?.length) return;

    this.router.navigate(['search'], { queryParams: { query } });

    (window as any).prefixboxAnalytics?.('query', 'submit', {
      pattern: query,
      btr: this.prefixboxService.getAutocompleteBoxTracker(this.device),
      content: this.apiResponse,
    });
    this.onWrapperClose();
  }

  public onViewMoreClick(): void {
    const query = this.suggestionsInput.searchBox.nativeElement.value.trim();
    this.router.navigate(['search'], { queryParams: { query, searchByName: true } });
    this.searchHistoryPersistenceService.add(query);

    (window as any).prefixboxAnalytics?.('query', 'submit', {
      pattern: query,
      btr: this.prefixboxService.getAutocompleteBoxTracker(this.device),
      content: this.apiResponse,
    });

    this.onWrapperClose();
  }

  public onBrandSuggestionClick(brand: string): void {
    this.hasQuery = false;
    this.router.navigate(['search'], { queryParams: { brand } });
    this.onWrapperClose();
  }

  public close(): void {
    this.searchInputFocus = false;
    this.restoreScroll();
    this.onCloseSearch();
  }

  clearSuggestions(): void {
    this.suggestions = {
      keywords: [],
      categories: [],
      products: [],
      contentPages: [],
      questions: [],
    };
  }

  public inputValueChanged(query: string) {
    this.query = query;
    this.clearSuggestions();

    if (!query) return;

    this.prefixboxService
      .getSuggestions(this.device, query)
      .pipe(take(1))
      .subscribe((data: any) => {
        if (
          !this.apiResponse ||
          !isEqual(
            this.apiResponse.map((value) => value.text),
            data.suggestions.map((value) => value.text)
          )
        ) {
          (window as any).prefixboxAnalytics?.('boxrender', 'autocomplete', {
            pattern: query,
            btr: this.prefixboxService.getAutocompleteBoxTracker(this.device),
            content: data.suggestions,
          });
        }

        this.apiResponse = data.suggestions;
        this.clearSuggestions();

        data.suggestions.forEach((item, index) => {
          switch (item.grp) {
            case '1':
              this.suggestions.keywords.push({ ...item, index: this.suggestions.keywords.length });
              break;
            case '2':
              this.suggestions.categories.push({
                ...item,
                index: this.suggestions.categories.length,
              });
              break;
            case '3':
              this.suggestions.products.push({ ...item, index: this.suggestions.products.length });
              break;
            case '4':
              this.suggestions.contentPages.push({
                ...item,
                index: this.suggestions.contentPages.length,
              });
              break;
            case '6':
              this.suggestions.questions.push({
                ...item,
                index: this.suggestions.questions.length,
              });
              break;
            default:
              break;
          }
        });
      });
  }

  public suggestionsInputReset(): void {
    this.suggestionsInput.searchBox.nativeElement.value = '';
    this.suggestionsInput.searchBox.nativeElement.focus();
  }

  public closeOnClickOutside(event: Event): void {
    if ((<HTMLElement>event.target).classList.contains('search-overlay')) {
      this.onWrapperClose();
    }
  }

  public isMobile(): boolean {
    const MEDIUM_SCREEN_MAX_WIDTH = 992;
    return window.innerWidth < MEDIUM_SCREEN_MAX_WIDTH;
  }

  public getHightlightedText(text: string): string {
    const query = this.query?.trim();
    if (!query?.length) return text;
    const re = new RegExp(query, 'gi');
    return text
      ?.replace(/<\/?b>/g, '')
      ?.replace(re, (str) => `<span class="highlighted-suggestion">${str}</span>`);
  }

  public getHightlightedTextFullWord(text: string): string {
    const query = this.query?.trim();
    if (!query?.length) return text;
    const re = new RegExp('(\\b)(\\w*' + query + '\\w*)(\\b)', 'ig');
    return text
      ?.replace(/<\/?b>/g, '')
      ?.replace(re, (str) => `<span class="highlighted-suggestion-bold">${str}</span>`);
  }

  private onWrapperClose() {
    this.suggestionsInputReset();
    this.inputValueChanged('');
    this.close();
  }

  private restoreScroll() {
    this.renderer.removeClass(this.document.body, 'blocked-scroll');
    this.document.body.classList.remove('modal-open');
  }
}
